/**
 * The navigation table: five sections, twenty-five tabs.
 *
 * ── STRINGS AND NOTHING ELSE ────────────────────────────────────────────────
 * `main.tsx` statically imports `App` → `AppShell` → this file, so everything
 * here lands in the entry chunk that a visitor downloads BEFORE the staff gate
 * answers. The property `App.tsx` claims — that a non-staff visitor never
 * fetches a chunk naming a single screen — holds only because no `import()`
 * executes outside a route rendered inside `<StaffOnly>`. A `component:` field
 * here, or a hover-prefetch handler, would quietly break that and nothing would
 * notice. `lazyBoundary.test.ts` asserts it.
 *
 * ── AND ZERO REDIRECTS ──────────────────────────────────────────────────────
 * All nineteen paths that already existed stay canonical; `/watch` is the only
 * addition. The output of this tool is a link somebody else opens later, so a
 * redirect would create a second name for every screen forever — and
 * `<Navigate to="/x"/>` DROPS the query string, which in this app means
 * silently answering a different question than the shared link asked. The five
 * sections partition them cleanly, so there is nothing to rename — including
 * `/data-quality` and `/admin`, which arrived with the write seam after this
 * table was written and needed no new section to hold them.
 */

export interface NavTab {
  to: string
  label: string
  /** Shown in the tab's tooltip: what this screen answers. */
  help?: string
}

export interface NavSection {
  id: string
  label: string
  /** The section's landing path — its first tab. */
  to: string
  tabs: NavTab[]
}

export const SECTIONS: NavSection[] = [
  {
    id: 'resumen',
    label: 'Resumen',
    to: '/',
    // The investor view is a ROUTE, not a `?view=` param and not local state.
    // `/investors` already exists and is already its own lazy chunk, the two
    // screens compose different endpoints, and a mode you cannot send to
    // somebody is not a mode in a tool whose output is a link.
    tabs: [
      { to: '/', label: 'Operación', help: 'Qué cambió desde ayer, y qué fuente está vieja.' },
      { to: '/investors', label: 'Inversionistas', help: 'Solo lo que está medido. Lo que no, se declara.' },
    ],
  },
  {
    id: 'clientes',
    label: 'Clientes',
    // La sección aterriza en su PRIMERA pestaña, que ahora es Soporte.
    // `nav.test.ts` lo afirma para las cinco, y con razón: una sección cuyo
    // enlace lleva a un sitio distinto del que resalta es una que el usuario
    // aprende a no creer.
    to: '/support',
    tabs: [
      // PRIMERA de la sección, y es lo único de esta consola que contiene
      // TRABAJO en vez de lectura: «¿alguien me está esperando?» es la pregunta
      // con la que alguien abre esta pestaña. Va en `Clientes` y no en una
      // sección propia porque un ticket es un cliente hablando, no maquinaria —
      // el mismo criterio con el que `/data-quality` y `/admin` entraron sin
      // necesitar una sección nueva.
      { to: '/support', label: 'Soporte', help: 'Quién espera respuesta, y desde cuándo. Ordenado por consecuencia.' },
      { to: '/orgs', label: 'Organizaciones', help: 'Quién existe, de qué clase, y qué hizo últimamente.' },
      { to: '/people', label: 'Personas', help: 'Quién usa esto de verdad. Sin historial de sesiones.' },
      { to: '/access', label: 'Solicitudes', help: 'Quién espera para entrar, y la decisión que lo dejó entrar o no.' },
      // Junto a Solicitudes y por la misma razón: una persona escribiendo,
      // antes de ser cliente. Lo que llega por fintela.io/contact.
      { to: '/contacts', label: 'Contactos', help: 'Quién escribió desde el sitio, qué pidió, y si el equipo ya se enteró.' },
      { to: '/funnel', label: 'Embudo', help: 'Dónde se detuvo cada organización. Una partición, no una cascada.' },
      { to: '/activity', label: 'Actividad', help: 'Lo último que pasó. Bitácora best-effort.' },
      // Aquí y no en «Plataforma», aunque escriban como `/admin`: las secciones
      // son las preguntas con las que alguien llega, no la forma de la base.
      // Quien abre esto viene pensando en a QUIÉN le va a escribir, que es la
      // misma pregunta que traen las otras cuatro pestañas de esta sección.
      { to: '/communications', label: 'Comunicados', help: 'Lo que le escribimos a la gente, por correo y dentro de la plataforma.' },
      { to: '/communications/audience', label: 'Audiencia', help: 'A quién le podemos escribir, a quién no, y qué significa cada audiencia.' },
      { to: '/communications/webinars', label: 'Webinars', help: 'Cuatro correos por evento. Registrados contra asistentes.' },
    ],
  },
  {
    id: 'dinero',
    label: 'Dinero',
    to: '/money',
    tabs: [
      { to: '/money', label: 'Escalera', help: 'De valor de lista a contribución, con los operadores a la vista.' },
      { to: '/money/margin', label: 'Margen', help: 'Qué operación deja algo, y cuánto se devuelve.' },
      { to: '/redemptions', label: 'Canjes', help: 'Foto de Stripe. La única fuente de los canjes.' },
      { to: '/tokens', label: 'Tokens', help: 'Débitos y créditos por razón, con el signo del ledger.' },
    ],
  },
  {
    id: 'costos',
    label: 'Costos',
    to: '/costs',
    tabs: [
      { to: '/costs', label: 'AWS', help: 'El mes cerrado más reciente, de una foto de Cost Explorer.' },
      { to: '/costs/compute', label: 'Cómputo', help: 'Cuánta memoria se reserva contra cuánta se usa.' },
      { to: '/storage', label: 'Almacenamiento', help: 'Estimado por coeficiente. El orden es exacto.' },
      { to: '/projections', label: 'Proyecciones', help: 'Qué se puede proyectar, y qué no.' },
      { to: '/calculator', label: 'Calculadora', help: 'Todo modelado. Ninguna cifra es dinero que haya entrado.' },
    ],
  },
  {
    id: 'plataforma',
    label: 'Plataforma',
    to: '/sources',
    tabs: [
      { to: '/sources', label: 'Fuentes', help: 'Qué colector está fresco, viejo o nunca corrió.' },
      { to: '/data-quality', label: 'Calidad de datos', help: 'Lo que la guarda no quiso ocultar sola. Espera un juicio humano.' },
      { to: '/watch', label: 'Alertas', help: 'Lo que la plataforma notó por su cuenta.' },
      { to: '/calibration', label: 'Calibración', help: 'Qué cobramos, qué asumimos, y cuándo cambió.' },
      // Último a propósito: es la única pantalla de la consola que escribe, y
      // el orden de la sección va de lo que la máquina reporta a lo que
      // nosotros cambiamos.
      { to: '/admin', label: 'Cambios', help: 'Lo único que esta consola modifica, con su bitácora.' },
    ],
  },
]

/** Every tab path, for the tests and for the route table's cross-check. */
export const ALL_TAB_PATHS: string[] = SECTIONS.flatMap((s) => s.tabs.map((t) => t.to))

/**
 * The active tab for a pathname, by LONGEST PREFIX.
 *
 * `/money/margin` must select «Margen», not «Escalera», even though `/money` is
 * a prefix of it — and `/costs/compute` the same. `/orgs/acme` must keep
 * «Organizaciones» selected even though it is not a tab. And `/` must match
 * only itself, or it would swallow every path in the app.
 *
 * Returns `null` when nothing matches, which is what keeps MUI from warning on
 * the not-found route.
 */
export function activeTab(pathname: string): string | null {
  let best: string | null = null
  for (const to of ALL_TAB_PATHS) {
    if (to === '/') {
      if (pathname === '/') return '/'
      continue
    }
    if ((pathname === to || pathname.startsWith(`${to}/`)) && (best === null || to.length > best.length)) {
      best = to
    }
  }
  return best
}

/** The section containing the active tab. */
export function activeSection(pathname: string): NavSection | null {
  const tab = activeTab(pathname)
  if (tab === null) return null
  return SECTIONS.find((s) => s.tabs.some((t) => t.to === tab)) ?? null
}

/** A section by id. Indexing `SECTIONS[0]` in the route table is a trap. */
export function sectionById(id: string): NavSection {
  const s = SECTIONS.find((x) => x.id === id)
  if (!s) throw new Error(`unknown section "${id}"`)
  return s
}
